import * as React from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId, Hub, noBoardContainerId} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {showConfirm} from '@components/common/base/notifications'
import {ProjectsFlatTreeDropdownContent} from '@components/common/projects-tree/projects-tree'
import {multiActionsWithTasksStore} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {SelectMultipleTasksButton} from '@components/multi-actions-with-tasks/select-multiple-tasks-button/select-multiple-tasks-button'
import {noPropagate, useBoard, useBoolean} from '@containers/hooks'
import {Column} from '@extensions/column'
import {GanttDraft} from '@extensions/gantt-draft'
import {Task} from '@extensions/task'
import {Menu} from '../menu'
import {TaskItem} from '../my-tasks/task-item'
import {useTaskCover} from './use-task-cover'

interface Props {
  task: TaskItem
  rename?: () => void
  removeFromSubtasks?: () => void
  addSubtask?: () => void
  duplicate?: boolean
  removeFromBoard?: boolean
  location: 'my-tasks' | 'board'
}

interface InnerProps {
  toggleComplete(): void
  toggleArchive(): void
  deleteTask(): void
  moveTask: (column: Hub[]) => void
  selectMultipleTasksToggleOn(): void
  canComplete: boolean
}

// eslint-disable-next-line max-lines-per-function
const TaskMenuBase = (props: Props & InnerProps) => {
  const {
    task,
    canComplete,
    toggleComplete,
    deleteTask,
    toggleArchive,
    rename,
    removeFromSubtasks,
    moveTask,
    addSubtask,
    selectMultipleTasksToggleOn,
    duplicate,
    removeFromBoard
  } = props
  const menuOpen = useBoolean(false)
  const moveOpen = useBoolean(false)
  const board = useBoard()
  const box = Sync.get(task)
  const hub = box.get(task.id) as Hub
  const makePublic = () => {
    delete hub.data.private
    hub.commit()
  }
  const bem = Util.bemFrom('task-menu')
  const chooseLocation = (locationId: DataObjectId) => {
    const loc = box.getHub(locationId)
    if (loc) {
      moveTask([loc])
      moveOpen.turnOff()
    }
  }
  const isWithoutBoard = task.location === noBoardContainerId
  const copyLink = () => Task.copyLink(task)
  const {uploadCover, removeCover} = useTaskCover(hub, 'board')

  return (
    <div className={bem()}>
      <div
        className={bem('button')}
        onClick={e => {
          e.stopPropagation()
          menuOpen.turnOn()
        }}
      >
        {moveOpen.value && (
          <Menu onEscape={moveOpen.turnOff}>
            <div className={bem('overlay')} onClick={moveOpen.turnOff} />
            <div className={bem('move')} onClick={noPropagate}>
              <ProjectsFlatTreeDropdownContent
                value={task.location}
                onChange={chooseLocation}
              />
            </div>
          </Menu>
        )}
        {menuOpen.value && (
          <Menu onEscape={menuOpen.turnOff}>
            <div className={bem('overlay')} onClick={menuOpen.turnOff} />
            <div
              className={bem('dropdown', {moveLeft: true})}
              onClick={e => {
                noPropagate(e)
                menuOpen.turnOff()
              }}
            >
              {addSubtask && (
                <div className={bem('dropdown', 'item')} onClick={addSubtask}>
                  <div
                    className={bem('dropdown', 'item', 'icon', {
                      addSubtask: true
                    })}
                  />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {Lang.msg.subtask.createSubtask}
                  </div>
                </div>
              )}

              {canComplete && (
                <div
                  className={bem('dropdown', 'item')}
                  onClick={toggleComplete}
                >
                  <div
                    className={bem('dropdown', 'item', 'icon', {
                      uncompleted: !task.completedAt,
                      completed: !!task.completedAt
                    })}
                  />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {task.completedAt > 0
                      ? Lang.msg.menuUncomplete
                      : Lang.msg.menuComplete}
                  </div>
                </div>
              )}

              {rename && (
                <div className={bem('dropdown', 'item')} onClick={rename}>
                  <div
                    className={bem('dropdown', 'item', 'icon', {
                      edit: true
                    })}
                  />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {Lang.msg.rename}
                  </div>
                </div>
              )}

              {task.isPrivate ? (
                <div className={bem('dropdown', 'item')} onClick={makePublic}>
                  <div
                    className={bem('dropdown', 'item', 'icon', {
                      move: true
                    })}
                  />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {Lang.msg.chat.makePublic}
                  </div>
                </div>
              ) : (
                <div
                  className={bem('dropdown', 'item')}
                  onClick={moveOpen.turnOn}
                >
                  <div
                    className={bem('dropdown', 'item', 'icon', {
                      move: true
                    })}
                  />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {Lang.msg.move}
                  </div>
                </div>
              )}

              <div className={bem('dropdown', 'item')} onClick={toggleArchive}>
                <div
                  className={bem('dropdown', 'item', 'icon', {
                    archive: true
                  })}
                />
                <div className={bem('dropdown', 'item', 'label')}>
                  {task.archivedAt
                    ? Lang.msg.menuUnarchive
                    : Lang.msg.menuArchive}
                </div>
              </div>

              {duplicate && (
                <div
                  className={bem('dropdown', 'item')}
                  onClick={() => Task.duplicate(hub)}
                >
                  <div
                    className={bem('dropdown', 'item', 'icon', {
                      duplicate: true
                    })}
                  />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {Lang.msg.duplicate}
                  </div>
                </div>
              )}

              <div className={bem('dropdown', 'item')} onClick={copyLink}>
                <div
                  className={bem('dropdown', 'item', 'icon', {copy: true})}
                />
                <div className={bem('dropdown', 'item', 'label')}>
                  {Lang.msg.chat.copyLinkOnTask}
                </div>
              </div>

              {board && (
                <div
                  className={bem('dropdown', 'item')}
                  onClick={selectMultipleTasksToggleOn}
                >
                  <SelectMultipleTasksButton />
                </div>
              )}

              <div className={bem('dropdown', 'item')} onClick={uploadCover}>
                <div
                  className={bem('dropdown', 'item', 'icon', {
                    'upload-cover': true
                  })}
                />
                <div className={bem('dropdown', 'item', 'label')}>
                  {Lang.msg.uploadTaskImage}
                </div>
              </div>

              {!!hub.data.image && (
                <div className={bem('dropdown', 'item')} onClick={removeCover}>
                  <div
                    className={bem('dropdown', 'item', 'icon', {
                      'remove-cover': true
                    })}
                  />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {Lang.msg.removeTaskImage}
                  </div>
                </div>
              )}

              {removeFromBoard && (
                <div
                  className={bem('dropdown', 'item')}
                  onClick={() => Column.removeFromBoard(hub)}
                >
                  <div className="dropdown-menu__dropdown__item__icon dropdown-menu__dropdown__item__icon--subtask" />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {Lang.msg.subtask.removeFromBoard}
                  </div>
                </div>
              )}

              <div className={bem('dropdown', 'item')} onClick={deleteTask}>
                <div
                  className={bem('dropdown', 'item', 'icon', {
                    delete: true
                  })}
                />
                <div className={bem('dropdown', 'item', 'label')}>
                  {Lang.msg.delete}
                </div>
              </div>

              {removeFromSubtasks && !isWithoutBoard && (
                <div
                  className={bem('dropdown', 'item')}
                  onClick={removeFromSubtasks}
                >
                  <div
                    className={bem('dropdown', 'item', 'icon', {
                      subtask: true
                    })}
                  />
                  <div className={bem('dropdown', 'item', 'label')}>
                    {Lang.msg.subtask.removeFromSubtasks}
                  </div>
                </div>
              )}
            </div>
          </Menu>
        )}
      </div>
    </div>
  )
}

export const TaskMenu = (props: Props) => {
  const {task} = props
  const box = Sync.getCurrentSandbox()
  const hub = box.get(task.id) as Hub

  const canComplete =
    PermissionsChecker.checkComplete(hub) &&
    (!PermissionsChecker.shouldFallbackToOldCheckComplete(hub) ||
      PermissionsChecker.oldCheckComplete(hub))

  const toggleComplete = () =>
    Task.changeCompleted(
      {...task, companyId: box.companyId},
      !task.completedAt,
      props.location
    )
  const toggleArchive = () =>
    Task.changeArchived({...task, companyId: box.companyId}, !task.archivedAt)

  const deleteTask = async () => {
    if (await showConfirm(Lang.msg.wantToDeleteATask)) {
      box.remove(hub)
      GanttDraft.planEntitySubtree(hub.id, box)
    }
  }

  const moveTask = (chosen: Hub[]) => {
    if (chosen.length > 0) {
      const column = chosen[0]
      Column.taskDropHandler(0, hub, column, {
        ignoreBoardFilters: true,
        type: 'menu'
      })
    }
  }

  const selectMultipleTasksToggleOn = () => {
    if (!multiActionsWithTasksStore.state.visible) {
      multiActionsWithTasksStore.visibleOn()
      void Tracking.send(TrackingEvent.multiActionsWithTasksSelectWithTaskMenu)
    }
    if (
      multiActionsWithTasksStore.state.visible &&
      multiActionsWithTasksStore.state.tasksIds.size === 0
    ) {
      multiActionsWithTasksStore.setTasksId(task.id)
    }
  }

  return (
    <TaskMenuBase
      {...props}
      canComplete={canComplete}
      toggleComplete={toggleComplete}
      toggleArchive={toggleArchive}
      deleteTask={deleteTask}
      moveTask={moveTask}
      selectMultipleTasksToggleOn={selectMultipleTasksToggleOn}
    />
  )
}
